import { useHistory } from 'react-router-dom'
import { useCenterStore } from '@caradasstore/CenterStore'
import { useEntitlementStore } from '@caradasstore/EntitlementStore'
import { useUserStore } from '@caradasstore/UserStore'
import { AdasBox } from '@components/wrapper-components'
import { DarkButton, StatusBanner } from './BillingUi'

const BILLING_PATH = '/settings?section=billing'

/**
 * App-wide billing state bar (PRD §6.2 #4), rendered by PageLayout under the broadcast bar.
 * Three cases, all for Stripe-rail centers only: a trial paused because trials are frozen,
 * no live subscription (everything module-gated is 402 on the API) and PAST_DUE (paused: a failed payment). Owners and managers get the button to Billing;
 * techs are told who to ask. Admins and Network accounts never see it.
 */
export const BillingStatusBar = () => {
  const history = useHistory()
  const user = useUserStore((store) => store.currentUser)
  const ccId = useCenterStore((store) => store.ccId)
  const { loadedForCcId, hasSubscription, state, managedExternally, trialPaused } = useEntitlementStore()

  if (!user || user.is_admin || managedExternally || !ccId || loadedForCcId !== ccId) return null

  const canManage = Boolean(user.is_owner || user.is_manager)
  const goBilling = () => history.push(BILLING_PATH)

  let banner = null
  if (trialPaused) {
    banner = (
      <StatusBanner
        tone='red'
        label='Trial paused'
        title='Trials are paused — this location is locked until it subscribes.'
        body={
          canManage
            ? 'Start your plan in Settings → Billing to unlock everything again. Your data is kept.'
            : 'Ask your account owner to subscribe in Settings → Billing.'
        }
        action={canManage ? <DarkButton onClick={goBilling}>Go to Billing</DarkButton> : null}
      />
    )
  } else if (hasSubscription === false) {
    banner = (
      <StatusBanner
        tone='amber'
        label='No package'
        title='This location has no active package — everything is locked until it has one.'
        body={
          canManage
            ? 'Choose a package in Settings → Billing to unlock everything.'
            : 'Ask your account owner to choose a package in Settings → Billing.'
        }
        action={canManage ? <DarkButton onClick={goBilling}>Choose a package</DarkButton> : null}
      />
    )
  } else if (state === 'PAST_DUE') {
    banner = (
      <StatusBanner
        tone='red'
        label='Payment failed'
        title='Your last payment failed — this location is paused until the payment goes through.'
        body={
          canManage
            ? 'Update the payment method in Settings → Billing to unlock everything again.'
            : 'Ask your account owner to update the payment method in Settings → Billing.'
        }
        action={canManage ? <DarkButton onClick={goBilling}>Update payment method</DarkButton> : null}
      />
    )
  }

  if (!banner) return null
  return <AdasBox className='px-3 min-[435px]:px-6 mx-auto w-full max-w-[1400px] mt-4 mb-4'>{banner}</AdasBox>
}
